// Turn a typed string into a swarm of letter bodies, spawning each letter at
// its actual rendered DOM position in the typing box. A multi-line note thus
// drops as multiple rows simultaneously instead of collapsing to one row first.

import Matter from 'matter-js';
import { createLetterBody } from '../physics/letterBody.js';
import { wakeAllBodies } from '../physics/engine.js';
import { state, makeStringId, type TypedString } from '../state.js';
import type { JarMessage } from '../shared/contracts.js';

// Match the typing-box CSS font so dropped letters render at the same size
// they had in the box (no visual "zoom" at release).
export const DROP_FONT_SIZE = 30;
export const DROP_FONT = `400 ${DROP_FONT_SIZE}px 'Henry'`;
const RADIUS_SCALE = 0.58;

export type DropResult = {
  stringId: string;
  bodies: Matter.Body[];
};

// Walk the typing box's rendered DOM and return per-character screen rects in
// reading order. Whitespace advances `charIndex` (so revive can match by index)
// but isn't returned, since we don't spawn bodies for whitespace.
export function probeCharRects(
  typingEl: HTMLElement,
): Array<{ char: string; charIndex: number; rect: DOMRect }> {
  const out: Array<{ char: string; charIndex: number; rect: DOMRect }> = [];
  const walker = document.createTreeWalker(typingEl, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let charIndex = 0;

  let node: Node | null;
  while ((node = walker.nextNode())) {
    const text = (node as Text).data;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i]!;
      if (ch === ' ' || ch === '\u00A0' || ch === '\n' || ch === '\t') {
        charIndex++;
        continue;
      }
      range.setStart(node, i);
      range.setEnd(node, i + 1);
      const rects = range.getClientRects();
      // Take the last client rect — when a glyph straddles a line-break, the
      // browser returns two rects; the last is where the glyph actually sits.
      const rect = rects[rects.length - 1] ?? range.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        out.push({ char: ch, charIndex, rect });
      }
      charIndex++;
    }
  }
  return out;
}

export function dropString(
  typingEl: HTMLElement,
  engine: Matter.Engine,
): DropResult | null {
  const text = typingEl.innerText;
  const trimmed = text.trim();
  if (!trimmed) return null;

  const stringId = makeStringId();
  const typedString: TypedString = {
    id: stringId,
    text: trimmed,
    createdAt: Date.now(),
  };
  state.strings.set(stringId, typedString);

  const chars = probeCharRects(typingEl);
  if (chars.length === 0) return null;

  const bodies: Matter.Body[] = [];
  for (const { char, charIndex, rect } of chars) {
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const radius =
      Math.max(rect.width, DROP_FONT_SIZE) / 2 * RADIUS_SCALE;
    const body = createLetterBody(char, x, y, radius, stringId, charIndex);

    // Near-rest release: gravity does the work. Tiny per-letter jitter so
    // identical starting rows don't land in a perfectly rigid pile.
    Matter.Body.setVelocity(body, {
      x: (Math.random() - 0.5) * 0.2,
      y: 0,
    });
    Matter.Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.02);

    bodies.push(body);
  }

  Matter.World.add(engine.world, bodies);
  state.lastSpawnTime = performance.now();

  // A new drop can plow through the pile and leave old letters floating above
  // empty space once sleeping bodies lose their support.
  wakeAllBodies(engine);

  return { stringId, bodies };
}

// Hydrate a previously-persisted message onto the canvas. The caller has
// already set the typing box text and forced layout — we only probe rects
// and create bodies, stamping the resulting TypedString with messageId and
// authorUserId so ownership can be checked on revive.
export function spawnMessageBodies(
  typingEl: HTMLElement,
  engine: Matter.Engine,
  msg: JarMessage,
): DropResult | null {
  const chars = probeCharRects(typingEl);
  if (chars.length === 0) return null;

  const stringId = makeStringId();
  const typedString: TypedString = {
    id: stringId,
    text: msg.body,
    createdAt: Date.parse(msg.createdAt) || Date.now(),
    messageId: msg.id,
    authorUserId: msg.userId,
    senderName: msg.senderName,
  };
  state.strings.set(stringId, typedString);

  const bodies: Matter.Body[] = [];
  for (const { char, charIndex, rect } of chars) {
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const radius =
      Math.max(rect.width, DROP_FONT_SIZE) / 2 * RADIUS_SCALE;
    const body = createLetterBody(char, x, y, radius, stringId, charIndex);
    Matter.Body.setVelocity(body, {
      x: (Math.random() - 0.5) * 0.2,
      y: 0,
    });
    Matter.Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.02);
    bodies.push(body);
  }

  Matter.World.add(engine.world, bodies);
  state.lastSpawnTime = performance.now();
  wakeAllBodies(engine);
  return { stringId, bodies };
}
